import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, StickyNote, Send, Trash2, Activity, Dumbbell, TrendingUp, Plus, Camera, Eraser, BellRing, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useUnits } from "@/lib/useUnits";
import WeaknessPicker from "@/components/WeaknessPicker";
import ClientNutritionCard from "@/components/ClientNutritionCard";
import ClientFoodLogCard from "@/components/ClientFoodLogCard";

const inputCls = "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

export default function ClientDetail() {
  const { id } = useParams();
  const [overview, setOverview] = useState(null);
  const [notes, setNotes] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [responses, setResponses] = useState({});
  const [expandedWo, setExpandedWo] = useState(null); // workout id
  const [allWorkouts, setAllWorkouts] = useState(null); // null until "load full history"
  const [loadingAll, setLoadingAll] = useState(false);
  const { fmtWeight } = useUnits();

  const loadAllWorkouts = async () => {
    setLoadingAll(true);
    try {
      const r = await api.get(`/coaching/clients/${id}/workouts`);
      setAllWorkouts(r.data || []);
    } catch {
      toast.error("Could not load full history");
    } finally {
      setLoadingAll(false);
    }
  };

  const deleteWorkout = async (workoutId) => {
    if (!window.confirm("Permanently delete this workout from the client's history?")) return;
    try {
      await api.delete(`/coaching/clients/${id}/workouts/${workoutId}`);
      toast.success("Workout deleted");
      setExpandedWo(null);
      setAllWorkouts((ws) => (ws ? ws.filter((w) => w.id !== workoutId) : ws));
      setOverview((o) => (o ? { ...o, workouts: (o.workouts || []).filter((w) => w.id !== workoutId) } : o));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete workout");
    }
  };

  const load = async () => {
    try {
      const [o, n, c, s] = await Promise.all([
        api.get(`/coaching/clients/${id}/overview`),
        api.get(`/coaching/clients/${id}/notes`),
        api.get(`/coaching/clients/${id}/checkins`),
        api.get(`/coaching/clients/${id}/snapshots`).catch(() => ({ data: [] })),
      ]);
      setOverview(o.data);
      setNotes(n.data);
      setCheckins(c.data);
      setSnapshots(s.data || []);
    } catch (e) {
      toast.error("Failed to load client");
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    try {
      await api.post(`/coaching/clients/${id}/notes`, { content: noteText });
      setNoteText("");
      toast.success("Note saved");
      load();
    } catch {
      toast.error("Failed to save");
    }
  };

  const deleteNote = async (nid) => {
    try {
      await api.delete(`/coaching/notes/${nid}`);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const respondCheckin = async (cid) => {
    const text = (responses[cid] || "").trim();
    if (!text) return;
    try {
      await api.post(`/coaching/checkins/${cid}/respond`, { response: text });
      setResponses((r) => ({ ...r, [cid]: "" }));
      toast.success("Reply sent");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const clearRoutines = async () => {
    const wipeLogs = window.confirm(
      "Un-assign every program from this client. Do you ALSO want to wipe every logged workout? " +
      "Cancel = keep the logs (recommended). OK = wipe logs too (destructive, cannot be undone)."
    );
    // If user hit Cancel above, ask a lighter confirm for the un-assign-only flow.
    if (!wipeLogs && !window.confirm(`Un-assign every program from ${c.first_name || c.email}?`)) {
      return;
    }
    try {
      const r = await api.delete(`/coaching/clients/${c.id}/assignments`, {
        params: { wipe_logs: wipeLogs },
      });
      const parts = [`Un-assigned ${r.data?.unassigned || 0} program${(r.data?.unassigned || 0) === 1 ? "" : "s"}`];
      if (r.data?.workouts_wiped) parts.push(`wiped ${r.data.workouts_wiped} logs`);
      toast.success(parts.join(" · "));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear routines");
    }
  };

  const remindCheckin = async () => {
    try {
      const r = await api.post(`/coaching/clients/${c.id}/remind-checkin`);
      const ch = r.data?.channels || {};
      const parts = [];
      if (ch.notif) parts.push("in-app");
      if (ch.push) parts.push(`${ch.push} push`);
      if (ch.email) parts.push("email");
      toast.success(`Reminder sent · ${parts.join(", ") || "queued"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remind");
    }
  };

  if (!overview) {
    return <div className="text-zinc-500 uppercase tracking-widest text-sm">Loading…</div>;
  }
  const c = overview.client;
  const p = overview.profile || {};
  const m = overview.macros || {};

  return (
    <div className="space-y-6">
      <Link to="/coach-dashboard" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white">
        <ArrowLeft className="h-3 w-3" /> Back to roster
      </Link>
      <div>
        <div className="overline text-blaze">/ Client</div>
        <h1 className="font-heading text-5xl uppercase mt-1">{c.first_name || c.email}</h1>
        <p className="text-zinc-400 text-sm mt-1">{c.email}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link
            to={`/coach-dashboard?assignTo=${c.id}`}
            data-testid="assign-workout-cta"
            className="blaze-btn px-4 py-2 text-xs inline-flex items-center gap-2"
          >
            <Plus className="h-3 w-3" /> Assign workout
          </Link>
          <button
            type="button"
            onClick={remindCheckin}
            data-testid="remind-checkin-cta"
            className="ghost-btn px-4 py-2 text-xs inline-flex items-center gap-2"
            title="Send in-app + push + email nudge"
          >
            <BellRing className="h-3 w-3" /> Remind check-in
          </button>
          <button
            type="button"
            onClick={clearRoutines}
            data-testid="clear-routines-cta"
            className="ghost-btn px-4 py-2 text-xs inline-flex items-center gap-2 text-red-400 hover:text-red-300"
            title="Un-assign every program (with optional log wipe)"
          >
            <Eraser className="h-3 w-3" /> Clear routines
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-tile"><div className="overline">Goal</div><div className="font-heading text-lg uppercase">{(p.fitness_goal || "—").replace("_", " ")}</div></div>
        <div className="stat-tile"><div className="overline">Weight</div><div className="font-data text-2xl">{p.weight_kg != null ? fmtWeight(p.weight_kg) : "—"}</div></div>
        <div className="stat-tile"><div className="overline">Calories</div><div className="font-data text-2xl">{m.calories ?? "—"}</div></div>
        <div className="stat-tile"><div className="overline">Workouts</div><div className="font-data text-2xl">{overview.workouts?.length || 0}</div></div>
      </div>

      <WeaknessPicker clientId={c.id} clientFirstName={c.first_name || c.email?.split("@")[0]} />

      <ClientNutritionCard clientId={c.id} />

      <ClientFoodLogCard clientId={c.id} />

      {/* Latest from this client — 5-second pulse before the coach drills in */}
      {(overview.workouts?.length || overview.weights?.length || overview.notes?.length) ? (
        <div className="obs-card" data-testid="client-pulse">
          <div className="flex items-center gap-3 mb-3">
            <Activity className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">Latest pulse</div>
              <h3 className="font-heading text-2xl uppercase">Last 7-day signal</h3>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(() => {
              const lastWo = overview.workouts?.[0];
              const lastWt = overview.weights?.[overview.weights.length - 1];
              const prevWt = overview.weights?.[overview.weights.length - 2];
              const topSet = lastWo?.exercises?.reduce((best, ex) => {
                for (const s of ex.sets || []) {
                  if (s.weight_kg > (best?.weight_kg ?? -1)) best = { ...s, name: ex.name };
                }
                return best;
              }, null);
              const lastNote = overview.notes?.[0] || notes?.[0];
              return (
                <>
                  <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
                    <div className="overline">Last workout</div>
                    {lastWo ? (
                      <>
                        <div className="font-heading text-sm uppercase truncate mt-1">{lastWo.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          {new Date(lastWo.created_at).toLocaleDateString()}
                        </div>
                      </>
                    ) : (
                      <div className="text-zinc-500 text-sm mt-1">—</div>
                    )}
                  </div>
                  <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
                    <div className="overline">Top set</div>
                    {topSet ? (
                      <>
                        <div className="font-data text-blaze text-sm mt-1">{fmtWeight(topSet.weight_kg)} × {topSet.reps}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{topSet.name}</div>
                      </>
                    ) : (
                      <div className="text-zinc-500 text-sm mt-1">—</div>
                    )}
                  </div>
                  <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
                    <div className="overline">Last weigh-in</div>
                    {lastWt ? (
                      <>
                        <div className="font-data text-sm mt-1">{fmtWeight(lastWt.weight_kg)}</div>
                        {prevWt && (
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                            Δ {(lastWt.weight_kg - prevWt.weight_kg).toFixed(1)}kg
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-zinc-500 text-sm mt-1">—</div>
                    )}
                  </div>
                  <div className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
                    <div className="overline">Last note</div>
                    {lastNote ? (
                      <div className="text-xs text-zinc-300 mt-1 line-clamp-3">{lastNote.body || lastNote.text}</div>
                    ) : (
                      <div className="text-zinc-500 text-sm mt-1">—</div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}
      <div className="obs-card" data-testid="client-workouts">
        <div className="flex items-center gap-3 mb-3">
          <Dumbbell className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Sessions logged</div>
            <h3 className="font-heading text-2xl uppercase">Workout history</h3>
          </div>
        </div>
        {(!overview.workouts || overview.workouts.length === 0) ? (
          <p className="text-zinc-500 text-sm">No workouts logged yet.</p>
        ) : (
          <>
            <p className="text-xs text-zinc-500 mb-3 uppercase tracking-widest">Click a session to see the full set-by-set breakdown.</p>
            <div className="space-y-2">
              {(allWorkouts ?? overview.workouts).map((w) => {
              const topSet = (w.exercises || []).reduce((best, ex) => {
                for (const s of ex.sets || []) {
                  if (s.weight_kg > (best?.weight_kg ?? -1)) {
                    best = { ...s, name: ex.name };
                  }
                }
                return best;
              }, null);
              const open = expandedWo === w.id;
              const hasPR = (w.exercises || []).some((ex) => (ex.sets || []).some((s) => s.is_pr));
              return (
                <div key={w.id} className={`border rounded-sm ${open ? "border-blaze/50" : "border-obsidian-600"}`}>
                  <button
                    type="button"
                    data-testid={`client-workout-row-${w.id}`}
                    onClick={() => setExpandedWo(open ? null : w.id)}
                    className="w-full text-left p-3 flex items-center justify-between gap-3 flex-wrap hover:bg-obsidian-800/40 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-lg uppercase truncate flex items-center gap-2">
                        <span className="truncate">{w.title}</span>
                        {hasPR && (
                          <span
                            data-testid={`client-workout-pr-chip-${w.id}`}
                            className="shrink-0 bg-blaze text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest"
                          >
                            PR
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        {new Date(w.created_at).toLocaleDateString()} · {(w.exercises || []).length} exercises · {(w.total_volume_kg || 0).toFixed(0)} kg volume
                      </div>
                    </div>
                    {topSet && (
                      <div className="text-right shrink-0">
                        <div className="overline">Top set</div>
                        <div className="font-data text-sm text-blaze">{fmtWeight(topSet.weight_kg)} × {topSet.reps}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{topSet.name}</div>
                      </div>
                    )}
                    {open ? <ChevronUp className="h-4 w-4 text-blaze shrink-0" /> : <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />}
                  </button>

                  {open && (
                    <div className="border-t border-obsidian-600 px-3 pb-3" data-testid={`client-workout-detail-${w.id}`}>
                      <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-zinc-500 py-2 flex-wrap">
                        <span>{new Date(w.created_at).toLocaleString()}</span>
                        {w.duration_min ? <span>{w.duration_min} min</span> : null}
                        {w.perceived_exertion ? <span>Effort {w.perceived_exertion}/10</span> : null}
                        <button
                          type="button"
                          data-testid={`delete-workout-${w.id}`}
                          onClick={() => deleteWorkout(w.id)}
                          className="ml-auto inline-flex items-center gap-1.5 text-red-400/80 hover:text-red-400 uppercase tracking-widest"
                          title="Permanently delete this workout"
                        >
                          <Trash2 className="h-3 w-3" /> Delete workout
                        </button>
                      </div>
                      {(w.exercises || []).map((ex, xi) => (
                        <div key={xi} className="mb-3">
                          <div className="font-heading text-sm uppercase text-zinc-200">{ex.name}</div>
                          <table className="w-full text-xs mt-1">
                            <thead>
                              <tr className="text-left text-[9px] uppercase tracking-widest text-zinc-500">
                                <th className="py-1 pr-3 font-medium">Set</th>
                                <th className="py-1 pr-3 font-medium">Reps</th>
                                <th className="py-1 pr-3 font-medium">Weight</th>
                                <th className="py-1 font-medium">RPE</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(ex.sets || []).map((s, si) => (
                                <tr key={si} className="border-t border-obsidian-700/50 text-zinc-300">
                                  <td className="py-1 pr-3">{si + 1}</td>
                                  <td className="py-1 pr-3">{s.reps ?? "—"}</td>
                                  <td className="py-1 pr-3">
                                    {s.weight_kg ? fmtWeight(s.weight_kg) : "—"}
                                    {s.is_pr && (
                                      <span
                                        data-testid="set-pr-badge"
                                        className="ml-1.5 bg-blaze text-white text-[9px] font-bold px-1 py-0.5 rounded-sm tracking-widest align-middle"
                                      >
                                        PR
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1">{s.rpe ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                      {w.notes && <p className="text-xs text-zinc-500 italic">“{w.notes}”</p>}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
            {allWorkouts === null && (overview.workouts?.length || 0) >= 20 ? (
              <button
                type="button"
                data-testid="client-workouts-load-all"
                onClick={loadAllWorkouts}
                disabled={loadingAll}
                className="ghost-btn px-4 py-2 text-xs mt-3 disabled:opacity-50"
              >
                {loadingAll ? "Loading…" : "Load full history"}
              </button>
            ) : allWorkouts !== null ? (
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3" data-testid="client-workouts-all-count">
                Showing all {allWorkouts.length} sessions
              </p>
            ) : null}
          </>
        )}
      </div>

      {snapshots.length > 0 && (
        <div className="obs-card" data-testid="client-snapshots">
          <div className="flex items-center gap-3 mb-3">
            <Camera className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">Public moments</div>
              <h3 className="font-heading text-2xl uppercase">Shared snapshots</h3>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-3 uppercase tracking-widest">
            PNGs this athlete generated to share. Great conversation starters.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {snapshots.slice(0, 12).map((s) => {
              const tok = localStorage.getItem("fp_token") || "";
              const src = `${process.env.REACT_APP_BACKEND_URL || ""}/api/snapshots/${s.id}/file?auth=${encodeURIComponent(tok)}`;
              return (
                <a
                  key={s.id}
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`client-snapshot-${s.id}`}
                  className="group relative border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900/40"
                >
                  <img src={src} alt="snapshot" loading="lazy" className="w-full aspect-square object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-obsidian-900/85 px-2 py-1 flex items-center justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-zinc-300">
                      {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                    <span className="text-blaze">{s.kind}</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {overview.weights && overview.weights.length > 1 && (
        <div className="obs-card" data-testid="client-weight-trend">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">Bodyweight trend</div>
              <h3 className="font-heading text-2xl uppercase">
                {fmtWeight(overview.weights[0].weight_kg)} → {fmtWeight(overview.weights[overview.weights.length - 1].weight_kg)}
              </h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {overview.weights.slice(-30).map((w, i) => (
              <div
                key={i}
                className="px-2 py-1 border border-obsidian-600 rounded-sm font-data text-xs text-zinc-300"
                title={new Date(w.created_at).toLocaleDateString()}
              >
                {fmtWeight(w.weight_kg)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="obs-card">
        <div className="flex items-center gap-3 mb-3">
          <StickyNote className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Coach notes</div>
            <h3 className="font-heading text-2xl uppercase">Private to you</h3>
          </div>
        </div>
        <form onSubmit={addNote} className="flex gap-2 mb-4">
          <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className={`${inputCls} flex-1`} />
          <button data-testid="note-save" type="submit" className="blaze-btn px-4 py-2 text-xs">Save</button>
        </form>
        {notes.length === 0 ? (
          <p className="text-zinc-500 text-sm">No notes yet.</p>
        ) : (
          <div className="space-y-2">
            {notes.map((n) => (
              <div key={n.id} className="border border-obsidian-600 rounded-sm p-3 flex justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 whitespace-pre-wrap">{n.content}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => deleteNote(n.id)} data-testid={`note-delete-${n.id}`} className="text-zinc-500 hover:text-blaze">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Check-ins */}
      <div className="obs-card">
        <div className="flex items-center gap-3 mb-3">
          <Activity className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Weekly check-ins</div>
            <h3 className="font-heading text-2xl uppercase">From the athlete</h3>
          </div>
        </div>
        {checkins.length === 0 ? (
          <p className="text-zinc-500 text-sm">No check-ins yet.</p>
        ) : (
          <div className="space-y-3" data-testid="client-checkins">
            {checkins.map((ci) => (
              <div key={ci.id} className="border border-obsidian-600 rounded-sm p-4 space-y-2">
                <div className="flex justify-between items-start gap-2 flex-wrap">
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="overline">Energy</span><span className="font-data">{ci.energy}/5</span>
                    {ci.sleep_hours != null && (<><span className="overline">Sleep</span><span className="font-data">{ci.sleep_hours}h</span></>)}
                    {ci.adherence_pct != null && (<><span className="overline">Adherence</span><span className="font-data">{ci.adherence_pct}%</span></>)}
                    {ci.weight_kg != null && (<><span className="overline">Weight</span><span className="font-data">{ci.weight_kg}kg</span></>)}
                  </div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                    {new Date(ci.created_at).toLocaleDateString()} · {ci.status}
                  </div>
                </div>
                {ci.notes && <div className="text-sm text-zinc-300 whitespace-pre-wrap">{ci.notes}</div>}
                {ci.answers && Object.keys(ci.answers).length > 0 && (
                  <div className="border-t border-obsidian-600 pt-2 space-y-2" data-testid={`ci-answers-${ci.id}`}>
                    <div className="overline text-blaze">Answers to your prompts</div>
                    {Object.entries(ci.answers).map(([q, a], i) => (
                      <div key={i} className="text-sm">
                        <div className="text-xs text-zinc-400">{q}</div>
                        <div className="text-zinc-200 whitespace-pre-wrap mt-0.5">{a || <span className="text-zinc-600 italic">(no answer)</span>}</div>
                      </div>
                    ))}
                  </div>
                )}
                {ci.coach_response ? (
                  <div className="bg-blaze/10 border border-blaze/40 rounded-sm p-2 text-sm">
                    <div className="overline text-blaze mb-1">Your reply</div>
                    <div className="text-zinc-200 whitespace-pre-wrap">{ci.coach_response}</div>
                  </div>
                ) : (
                  <div className="flex gap-2 pt-1">
                    <input
                      value={responses[ci.id] || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, [ci.id]: e.target.value }))}
                      placeholder="Write a quick reply..."
                      className={`${inputCls} flex-1`}
                    />
                    <button
                      data-testid={`respond-${ci.id}`}
                      onClick={() => respondCheckin(ci.id)}
                      className="blaze-btn px-3 py-2 text-xs inline-flex items-center gap-1.5"
                    >
                      <Send className="h-3 w-3" /> Send
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
